import { useWindowResize } from '@toggle/helpers';
import { TransformedXAxisType } from '@toggle/toggle';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useRef } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  SingleSeries,
} from '../apache-config';
import { tooltipFormatter } from '../utils';

const seriesOptions = {
  endLabel: {
    show: true,
    formatter: '{a}',
    distance: 20,
  },
  lineStyle: {
    width: 4,
  },
  smooth: true,
  symbolSize: 20,
};

export interface BumpChartProps extends ApacheChartBase {
  chartType: 'apache-bump';
  xAxis: TransformedXAxisType;
  series: SingleSeries[];
}

export const BumpChart = ({
  theme = 'dark_default',
  chartType,
  xAxis,
  series,
  onInit,
  isModal,
}: BumpChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    const chartDom = init(ref.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const seriesData: echarts.SeriesOption[] = series.map(i => ({
      ...seriesOptions,
      ...i,
      type: 'line' as const,
    }));

    const option = createApacheConfig({
      theme,
      isModal,
      options: {
        animationDuration: 0,
        tooltip: {
          trigger: 'item',
          formatter: params => {
            return tooltipFormatter({
              isShowTitle: true,
              isShowMarker: true,
              params: params as Types.DefaultLabelFormatterCallbackParams,
            });
          },
        },
        xAxis: {
          type: 'category',
          data: xAxis,
          boundaryGap: false,
          splitLine: {
            show: true,
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            margin: 20,
            formatter: '{value}',
          },
        },
        series: seriesData,
      },
    });

    chartDom.setOption(option);

    if (ref.current) {
      onInit?.(ref.current);
    }

    return () => {
      chartDom.dispose();
    };
  }, [chartType, theme, xAxis, series, width, height, isModal]);

  return <div ref={ref} />;
};
